import React from 'react';
import './TranslationLoadingScreen.css';

const TranslationLoadingScreen = ({
  currentStep = 'initializing',
  progress = 0,
  currentLanguage = '',
  totalLanguages,
  completedLanguages = 0,
  error = null,
  onCancel = null,
  languageList = null
}) => {
  // Build language cards from the dynamic languageList prop, or fall back to a generic display
  const langCards = languageList && languageList.length > 0
    ? languageList.map((lang, idx) => ({
        name: lang.name,
        flag: lang.flag,
        completed: completedLanguages >= idx + 1,
        isCurrent: currentLanguage === lang.name
      }))
    : [];

  const effectiveTotalLanguages = totalLanguages || (langCards.length > 0 ? langCards.length : 1);

  const steps = {
    initializing: {
      title: '🚀 Initializing Translation',
      description: 'Preparing your content for multi-language translation...'
    },
    translating: {
      title: '🌐 Translating Content',
      description: currentLanguage ? `Translating to ${currentLanguage}...` : 'Translating content...'
    },
    finalizing: {
      title: '✨ Finalizing Translations',
      description: 'Processing and organizing all language versions...'
    },
    completed: {
      title: '✅ Translation Complete',
      description: 'All translations are ready for review!'
    },
    error: {
      title: '❌ Translation Error',
      description: error || 'An error occurred during translation. Please try again.'
    }
  };

  const currentStepInfo = steps[currentStep] || steps.initializing;

  return (
    <div className="translation-loading-overlay">
      <div className="translation-loading-modal">
        {/* Header */}
        <div className="loading-header">
          <div className="loading-icon">
            <div className="globe-spinner"></div>
          </div>
          <h2>{currentStepInfo.title}</h2>
          <p>{currentStepInfo.description}</p>
        </div>

        {/* Progress Bar */}
        <div className="progress-section">
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="progress-text">
            {Math.round(progress)}% Complete
          </div>
        </div>

        {/* Language Status — only render if we have language cards */}
        {langCards.length > 0 && (
          <div className="languages-section">
            <h3>Translation Progress</h3>
            <div className="languages-grid">
              {langCards.map((lang) => (
                <div
                  key={lang.name}
                  className={`language-item ${lang.completed ? 'completed' : ''} ${
                    lang.isCurrent ? 'current' : ''
                  }`}
                >
                  <div className="language-flag">{lang.flag}</div>
                  <div className="language-name">{lang.name}</div>
                  <div className="language-status">
                    {lang.completed ? (
                      <span className="status-icon completed">✅</span>
                    ) : lang.isCurrent ? (
                      <span className="status-icon translating">⏳</span>
                    ) : (
                      <span className="status-icon pending">⏸</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status Messages */}
        <div className="status-section">
          <div className="status-message">
            {currentStep === 'translating' && (
              <div className="translating-message">
                <span className="translating-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
                Translating content using AI...
              </div>
            )}
            {currentStep === 'initializing' && (
              <div className="initializing-message">
                <span className="pulse-dot"></span>
                Connecting to translation service...
              </div>
            )}
            {currentStep === 'finalizing' && (
              <div className="finalizing-message">
                <span className="checkmark-animation">✓</span>
                Organizing translations...
              </div>
            )}
            {currentStep === 'completed' && (
              <div className="completed-message">
                <span className="success-icon">🎉</span>
                Ready for review!
              </div>
            )}
          </div>
        </div>

        {/* Estimated Time */}
        <div className="time-section">
          <div className="time-estimate">
            <span className="time-icon">⏱️</span>
            <span>This will take a few minutes</span>
          </div>
          <div className="helpful-tip">
            💡 We're using AI to provide accurate, contextual translations for tourism content
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-section" style={{
            background: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            padding: '15px',
            margin: '20px 0',
            color: '#c33'
          }}>
            <h4>⚠️ Translation Error</h4>
            <p>{error}</p>
          </div>
        )}

        {/* Cancel Option */}
        <div className="action-section">
          <button
            className="cancel-translation-btn"
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else if (window.confirm('Are you sure you want to cancel the translation process?')) {
                window.location.reload();
              }
            }}
          >
            {error ? 'Go Back' : 'Cancel Translation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TranslationLoadingScreen;
